import { createStore, useStore } from '../../lib/store';

// Phone or desktop layout (contract §1.6). The app is phone-first; a window at
// least 900 px wide and 500 px tall (a computer, a tablet in landscape) gets
// the desktop layout: a sidebar instead of the bottom nav bar, a full-width
// now-playing bar instead of the mini player, wider pages and centred dialogs
// instead of bottom sheets. Phones in landscape stay under 500 px tall, so
// they keep the phone layout.
//
// `<html data-layout="desktop">` carries it to CSS (absent on phones), so a
// module styles its desktop variant with `:global(html[data-layout='desktop']) .x`.

export type LayoutMode = 'phone' | 'desktop';

export const DESKTOP_LAYOUT_QUERY = '(min-width: 900px) and (min-height: 500px)';

const ATTRIBUTE = 'data-layout';

function queryLayout(win: Window | undefined): LayoutMode {
  if (!win || typeof win.matchMedia !== 'function') return 'phone';
  return win.matchMedia(DESKTOP_LAYOUT_QUERY).matches ? 'desktop' : 'phone';
}

/** The layout in effect; follows the window size once `initLayout()` ran. */
export const layoutStore = createStore<LayoutMode>(queryLayout(typeof window === 'undefined' ? undefined : window));

function reflect(mode: LayoutMode, doc: Document): void {
  if (mode === 'desktop') doc.documentElement.setAttribute(ATTRIBUTE, 'desktop');
  else doc.documentElement.removeAttribute(ATTRIBUTE);
}

/**
 * Applies the layout to `<html>` before the first frame and follows window
 * resizes (main.tsx). Returns a function that stops following them.
 */
export function initLayout(win: Window = window): () => void {
  const apply = () => {
    const mode = queryLayout(win);
    reflect(mode, win.document);
    layoutStore.set(mode);
  };
  apply();
  if (typeof win.matchMedia !== 'function') return () => {};
  const query = win.matchMedia(DESKTOP_LAYOUT_QUERY);
  query.addEventListener('change', apply);
  return () => query.removeEventListener('change', apply);
}

/** Sets the layout directly (tests, the design-system gallery). */
export function setLayoutMode(mode: LayoutMode, doc: Document = document): void {
  reflect(mode, doc);
  layoutStore.set(mode);
}

export function isDesktopLayout(): boolean {
  return layoutStore.get() === 'desktop';
}

/** True while the desktop layout is in effect; re-renders when the window crosses the breakpoint. */
export function useDesktopLayout(): boolean {
  return useStore(layoutStore) === 'desktop';
}
